/* ────────────────────────────────────────────────────────────────────────────
   SPLENDIDO — coordinatore del carrello                              2026-08-04
   ────────────────────────────────────────────────────────────────────────────
   Perché questo file esiste (e perché NON sta più dentro la sezione del cassetto):

   Prima, questa logica viveva dentro `sections/cart-drawer.liquid`, nel ramo
   Liquid „carrello NON vuoto". Conseguenza: quando la pagina veniva generata
   (o ripescata dalla cache del browser) con il carrello vuoto, il codice
   semplicemente NON esisteva nella pagina. Ed è esattamente lo scenario visto
   nella registrazione Clarity: cassetto vecchio che dice „vuoto", il cliente
   riprova ad aggiungere, e al posto del nostro messaggio gentile compariva il
   banner rosso di Prestige „Die maximale Menge … befindet sich bereits in
   deinem Warenkorb". Qui invece il file è caricato dal layout su ogni pagina.

   Cosa fa, in ordine:

   1) ALLINEAMENTO — al caricamento confronta il carrello VERO (`/cart.js`) con
      quello che era stato disegnato nella pagina (`window.__spCartRendered`,
      scritto da `layout/theme.liquid`). Il confronto è sulla FIRMA completa
      („chiave riga : quantità" per ogni riga), non sul solo numero di pezzi:
      un carrello con 1 pezzo diverso ha lo stesso conteggio ma contenuto
      diverso. Se differiscono lancia `cart:refresh`, l'evento ufficiale di
      Prestige che ridisegna insieme contatore, pallino e contenuto del cassetto.

   2) ARTICOLO GIÀ NEL CARRELLO — NON blocchiamo più il click a priori (il
      vecchio codice teneva un elenco di varianti e fermava il submit: sui pochi
      articoli con scorta > 1 impediva un acquisto legittimo, e un click veloce
      poteva comunque precedere la risposta). Ora lasciamo rispondere Shopify,
      che è l'unico a sapere quante scorte restano, e intercettiamo l'errore:
      se la variante è già nel carrello, il banner rosso viene soppresso, il
      cassetto si apre, la riga si illumina e sotto compare la frase tranquilla.
      Il banner si sopprime perché Prestige lo aggiunge da un listener legato al
      FORM (`BuyButtons`), mentre noi ascoltiamo su `document` in fase di
      cattura: il nostro gestore corre prima e ferma la propagazione.
      Per ogni ALTRO errore non tocchiamo niente: resta il comportamento di oggi
      (toast + messaggio di Shopify, che è già tradotto e più preciso del nostro).

   3) UNA SOLA RICHIESTA PER VOLTA — le letture di `/cart.js` passano tutte da
      qui: se una è già in volo la si riusa, e le risposte superate da una più
      recente vengono scartate (numero progressivo), così una risposta lenta non
      può più sovrascrivere uno stato più aggiornato. Dopo `cart:change` non
      chiediamo nulla: il carrello arriva già dentro l'evento.

   Se questo file non si carica, si torna esattamente al comportamento standard
   di Prestige (banner rosso): nessun acquisto viene bloccato.
   ──────────────────────────────────────────────────────────────────────────── */

(function () {
  'use strict';

  if (window.__spCartCoordinator) return;
  window.__spCartCoordinator = true;

  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var locale = (document.documentElement.lang || 'de').slice(0, 2).toLowerCase();

  /* Frase mostrata sotto la riga del gioiello già presente nel carrello. */
  var NOTE = {
    de: 'Dieser Artikel liegt bereits in Ihrem Warenkorb – schließen Sie Ihren Kauf an der Kasse ab.',
    en: 'This item is already in your cart – complete your purchase at checkout.',
    fr: 'Cet article se trouve déjà dans votre panier – finalisez votre achat à la caisse.',
    nl: 'Dit artikel zit al in uw winkelwagen – rond uw aankoop af bij de kassa.',
    pl: 'Ten artykuł już znajduje się w Twoim koszyku – dokończ zakup przy kasie.',
    es: 'Este artículo ya está en su carrito – complete su compra en la caja.'
  };
  var noteText = NOTE[locale] || NOTE.de;

  /* Messaggio di ripiego del toast, quando Shopify non ci dà un testo. */
  var GENERICO = {
    de: 'Konnte nicht zum Warenkorb hinzugefügt werden',
    en: 'Could not add to cart',
    fr: 'Impossible d’ajouter au panier',
    pl: 'Nie można dodać do koszyka',
    es: 'No se pudo añadir al carrito',
    nl: 'Kon niet aan winkelwagen toevoegen'
  };
  var genericoText = GENERICO[locale] || GENERICO.de;

  /* ── Stato del carrello ────────────────────────────────────────────────── */

  /* Varianti attualmente nel carrello (stringhe). Serve per decidere, nel
     momento esatto dell'errore e senza poter aspettare una richiesta, se si
     tratta di „ce l'ha già" oppure di un errore diverso. */
  var inCart = new Set();
  var ready = false;   /* false finché non abbiamo letto il carrello almeno una volta */

  var seq = 0;         /* numero progressivo: scarta le risposte superate */
  var pending = null;  /* richiesta in volo, riutilizzabile */

  function firma(cart) {
    return (cart.items || [])
      .map(function (i) { return i.key + ':' + i.quantity; })
      .join('|');
  }

  function applica(cart) {
    inCart = new Set((cart.items || []).map(function (i) { return String(i.variant_id || i.id); }));
    ready = true;
    return cart;
  }

  function leggiCarrello() {
    if (pending) return pending;
    var mio = ++seq;
    pending = fetch(root + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) {
        if (!r.ok) throw new Error('cart.js ' + r.status);
        return r.json();
      })
      .then(function (cart) {
        pending = null;
        if (mio !== seq) return null;   /* superata da una richiesta più recente */
        return applica(cart);
      })
      .catch(function () {
        pending = null;
        return null;
      });
    return pending;
  }

  /* `cart:change` porta già il carrello aggiornato nell'evento: nessuna
     richiesta in più, e invalida eventuali risposte ancora in volo. */
  document.addEventListener('cart:change', function (e) {
    if (e && e.detail && e.detail.cart) { seq++; applica(e.detail.cart); }
    else leggiCarrello();
  });
  document.addEventListener('cart:refresh', function () { leggiCarrello(); });

  /* ── 1) Allineamento pagina ↔ carrello vero ────────────────────────────── */

  function allinea() {
    var disegnato = typeof window.__spCartRendered === 'string' ? window.__spCartRendered : null;
    leggiCarrello().then(function (cart) {
      if (!cart || disegnato === null) return;
      if (firma(cart) === disegnato) return;   /* pagina fresca: niente da fare */
      document.dispatchEvent(new CustomEvent('cart:refresh'));
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', allinea, { once: true });
  } else {
    allinea();
  }

  /* Ritorno dalla cronologia (avanti/indietro) e rientro sulla scheda: la
     pagina può essere ferma a com'era. Prestige ricontrolla solo su `pageshow`
     con `persisted`; qui copriamo anche il rientro sulla scheda. */
  window.addEventListener('pageshow', function (e) { if (e.persisted) allinea(); });
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'visible') allinea();
  });

  /* ── 2) „Questo pezzo è già nel suo carrello" ──────────────────────────── */

  function cassetto() { return document.getElementById('cart-drawer'); }
  function cassettoAperto() { var d = cassetto(); return !!(d && d.hasAttribute('open')); }

  function apriCassetto() {
    if (cassettoAperto()) return;
    var trigger = document.querySelector('[aria-controls="cart-drawer"]');
    if (trigger) trigger.click();
  }

  function pulisciNote() {
    var d = cassetto(); if (!d) return;
    d.querySelectorAll('.sp-already-note').forEach(function (n) { n.remove(); });
    d.querySelectorAll('.sp-already-highlight').forEach(function (r) { r.classList.remove('sp-already-highlight'); });
  }

  function evidenzia(variantId) {
    var d = cassetto(); if (!d) return false;
    var row = d.querySelector('line-item[data-variant-id="' + variantId + '"]');
    if (!row) return false;
    try { row.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (err) {}
    row.classList.remove('sp-already-highlight');
    void row.offsetWidth;                 /* fa ripartire l'animazione */
    row.classList.add('sp-already-highlight');
    var host = row.querySelector('.line-item-info') || row;
    var vecchia = host.querySelector('.sp-already-note');
    if (vecchia) vecchia.remove();
    var note = document.createElement('div');
    note.className = 'sp-already-note';
    note.setAttribute('role', 'status');
    note.innerHTML = '<span class="sp-already-note__icon" aria-hidden="true">✓</span><span></span>';
    note.lastChild.textContent = noteText;
    host.appendChild(note);
    return true;
  }

  /* Quale riga va evidenziata appena il cassetto è pronto.
     Serve perché dopo l'errore Prestige lancia `cart:refresh`: il cassetto
     richiede la sezione al server e ne SOSTITUISCE tutto il contenuto
     (`replaceChildren`), portandosi via la nota che avessimo già messo.
     Quindi non basta scriverla una volta: va riscritta dopo ogni ridisegno,
     finché la richiesta è ancora attuale. */
  var daEvidenziare = null;

  document.addEventListener('cart-drawer:refreshed', function () {
    if (daEvidenziare) evidenzia(daEvidenziare);
  });

  function mostraGiaPresente(variantId) {
    daEvidenziare = variantId;
    apriCassetto();

    /* Primo tentativo subito, poi a piccoli passi finché la riga non c'è. */
    var tentativi = 0;
    (function attendi() {
      tentativi++;
      if (cassettoAperto() && evidenzia(variantId)) return;
      if (tentativi < 40) setTimeout(attendi, 50);   /* al massimo 2 secondi */
    })();

    /* Dopo qualche secondo la richiesta non è più „attuale": smettiamo di
       riscrivere la nota a ogni ridisegno (altrimenti tornerebbe anche molto
       più tardi, per esempio dopo un cambio di quantità). */
    setTimeout(function () {
      if (daEvidenziare === variantId) daEvidenziare = null;
    }, 5000);
  }

  /* ── 2b) IL CASSETTO NON PUÒ RESTARE VUOTO SE IL CARRELLO È PIENO ──────────
     Rete di sicurezza indipendente dalla CAUSA. Il punto 1) copre il caso „pagina
     vecchia al caricamento", ma il cassetto può risultare disallineato anche per
     altre strade (ritorno dalla cronologia, richiesta di sezione fallita per rete
     ballerina, pagina rimasta aperta a lungo). Qui non si indaga il perché: ogni
     volta che il cassetto si APRE — e subito dopo un errore di aggiunta — si
     confronta ciò che il cassetto mostra con il carrello vero e, se non
     combaciano, si chiede a Prestige di ridisegnarlo.
     Registrazione Clarity del 06/08/2026: cassetto „Ihr Schmuckkästchen ist noch
     leer" mentre il server rispondeva „la quantità massima è già nel carrello".
     Verificato via HTTP che pagina, `/cart.js` e la sezione del cassetto sono
     SEMPRE corrette lato server ⇒ il disallineamento sta nel browser. */
  var ultimoControllo = 0;

  function righeMostrate() {
    var d = cassetto(); if (!d) return -1;
    return d.querySelectorAll('line-item').length;
  }

  function verificaCoerenza() {
    var d = cassetto(); if (!d) return;
    var adesso = Date.now();
    if (adesso - ultimoControllo < 700) return;   /* niente raffiche */
    ultimoControllo = adesso;
    leggiCarrello().then(function (cart) {
      if (!cart) return;
      var mostrate = righeMostrate();
      if (mostrate < 0) return;
      var attese = (cart.items || []).length;
      /* pieno ma disegnato vuoto (il caso della registrazione) oppure il contrario */
      if ((attese > 0 && mostrate === 0) || (attese === 0 && mostrate > 0)) {
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      }
    });
  }

  /* Chiudendo il cassetto le note spariscono; aprendolo si controlla la coerenza. */
  (function () {
    var d = cassetto(); if (!d) return;
    new MutationObserver(function () {
      if (d.hasAttribute('open')) verificaCoerenza();
      else { daEvidenziare = null; pulisciNote(); }
    }).observe(d, { attributes: true, attributeFilter: ['open'] });
  })();

  /* Quale variante stava tentando di aggiungere il form che ha dato errore. */
  function varianteDelForm(form) {
    if (!form) return null;
    var id = null;
    try { id = new FormData(form).get('id'); } catch (err) {}
    if (!id) { var el = form.querySelector('[name="id"]'); id = el && el.value; }
    return id ? String(id) : null;
  }

  /* ── 3) Toast per gli ALTRI errori ─────────────────────────────────────── */

  /* Serve dove il banner rosso di Prestige non si vede: l'acquisto rapido dalle
     pagine di categoria. Prima stava dentro la sezione del cassetto, quindi
     mancava proprio quando il carrello risultava vuoto. */
  function toast(messaggio) {
    var vecchio = document.querySelector('.splendido-toast');
    if (vecchio) vecchio.remove();
    var el = document.createElement('div');
    el.className = 'splendido-toast';
    el.setAttribute('role', 'status');
    el.setAttribute('aria-live', 'polite');
    el.textContent = messaggio;
    document.body.appendChild(el);
    requestAnimationFrame(function () { el.classList.add('is-visible'); });
    setTimeout(function () {
      el.classList.remove('is-visible');
      setTimeout(function () { el.remove(); }, 400);
    }, 3500);
  }

  /* FASE DI CATTURA su `document`: corre prima del listener di Prestige, che è
     legato al form stesso (BuyButtons → banner rosso). */
  document.addEventListener('cart:error', function (e) {
    var testo = (e && e.detail && (e.detail.error || e.detail.message)) || '';
    var form = e.target && e.target.closest ? e.target.closest('form') : e.target;
    var id = varianteDelForm(form);

    /* Il banner rosso di Prestige non deve comparire MAI: è scritto in „du"
       („…bereits in deinem Warenkorb", testo di sistema Shopify, non modificabile
       dal tema) e su questo negozio si dà sempre del Lei. Si ferma qui, sempre,
       e poi si decide con calma cosa dire.
       ⚠️ Prima si fermava solo se il carrello era GIÀ stato letto: chi premeva nei
       primissimi istanti dopo l'apertura della pagina — o dopo un ritorno dalla
       cronologia — vedeva il rosso. È lo scenario della registrazione del 06/08. */
    e.stopPropagation();
    e.stopImmediatePropagation();

    if (ready && id && inCart.has(id)) {
      mostraGiaPresente(id);
      verificaCoerenza();
      return;
    }

    /* Non sappiamo ancora cosa c'è nel carrello: lo chiediamo e decidiamo dopo.
       Un attimo di attesa è meglio di un messaggio sbagliato. */
    leggiCarrello().then(function (cart) {
      if (cart && id && inCart.has(id)) {
        mostraGiaPresente(id);
        verificaCoerenza();
      } else {
        toast(testo || genericoText);
      }
    });
  }, true);

  /* ── 4) MISURA · „Weiter einkaufen" · „Passt dazu"  (2026-09-11) ─────────────
     Misura: fino a oggi non sapevamo quanti aprono il cassetto e quanti premono
     „Zur Kasse". Stesso schema della finestra paese (`sp_land_*`): Clarity +
     dataLayer + Shopify.analytics, e non deve mai rompere niente. */

  function misura(nome, dati) {
    var d = dati || {};
    try { if (typeof window.clarity === 'function') { window.clarity('event', nome); window.clarity('set', 'sp_cart', nome); } } catch (e) {}
    try { (window.dataLayer = window.dataLayer || []).push(Object.assign({ event: nome }, d)); } catch (e) {}
    try { if (window.Shopify && window.Shopify.analytics && window.Shopify.analytics.publish) window.Shopify.analytics.publish(nome, d); } catch (e) {}
  }

  (function () {
    var d = cassetto(); if (!d) return;
    var eraAperto = d.hasAttribute('open');
    new MutationObserver(function () {
      var aperto = d.hasAttribute('open');
      if (aperto === eraAperto) return;
      eraAperto = aperto;
      if (aperto) { misura('sp_cart_open', { righe: righeMostrate() }); riempiPasst(); }
      else misura('sp_cart_close');
    }).observe(d, { attributes: true, attributeFilter: ['open'] });
    if (eraAperto) riempiPasst();
  })();

  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;
    if (t.closest('cart-drawer .button-group [name="checkout"]')) misura('sp_cart_checkout', { righe: righeMostrate() });
    else if (t.closest('cart-drawer .line-item__remove')) misura('sp_cart_remove');
    else if (t.closest('cart-drawer .sp-cart-express')) misura('sp_cart_express');
  }, true);

  /* „Weiter einkaufen": chiude il cassetto con il metodo di Prestige (hide → animazione + focus). */
  document.addEventListener('click', function (e) {
    var b = e.target && e.target.closest && e.target.closest('[data-sp-cart-close]');
    if (!b) return;
    e.preventDefault();
    var d = cassetto(); if (!d) return;
    misura('sp_cart_continue');
    if (typeof d.hide === 'function') d.hide(); else d.removeAttribute('open');
  });

  /* „Passt dazu": due proposte automatiche legate all'ultimo pezzo aggiunto
     (API recommendations di Shopify: prima `complementary`, poi `related` se non
     bastano). Il contenitore lo disegna Liquid solo con ≤ 2 righe nel carrello e
     con i prodotti già presenti esclusi; qui si riempie. Solo prodotti disponibili
     con UNA variante: con più varianti il „+" aggiungerebbe una scelta al buio.
     Se l'API tace, il blocco resta nascosto: nessun errore visibile. */
  var passtCache = {};

  function soldi(prezzo) {
    var cents = typeof prezzo === 'string' && prezzo.indexOf('.') !== -1 ? Math.round(parseFloat(prezzo) * 100) : Number(prezzo);
    try {
      var lang = document.documentElement.lang || 'de';
      var cur = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'EUR';
      return new Intl.NumberFormat(lang, { style: 'currency', currency: cur }).format(cents / 100);
    } catch (e) { return (cents / 100).toFixed(2) + ' €'; }
  }

  /* 2026-09-11: vale anche per la PAGINA /cart (main-cart.liquid ha lo stesso blocco).
     Nel cassetto si riempie solo quando è aperto; nella pagina subito. */
  function riempiPasst() {
    var d = cassetto();
    if (d && cassettoAperto()) riempiPasstBox(d.querySelector('[data-sp-passt]'));
    Array.prototype.forEach.call(document.querySelectorAll('[data-sp-passt]'), function (b) {
      if (!b.closest('cart-drawer')) riempiPasstBox(b);
    });
  }
  function riempiPasstBox(box) {
    if (!box || box.getAttribute('data-sp-done')) return;
    var seed = box.getAttribute('data-sp-seed');
    if (!seed) return;
    box.setAttribute('data-sp-done', '1');
    var esclusi = (box.getAttribute('data-sp-exclude') || '').split(',');
    var etichetta = box.getAttribute('data-sp-add') || '+';

    function prendi(intent) {
      return fetch(root + 'recommendations/products.json?product_id=' + encodeURIComponent(seed) + '&limit=6&intent=' + intent)
        .then(function (r) { return r.ok ? r.json() : { products: [] }; })
        .then(function (j) { return j.products || []; })
        .catch(function () { return []; });
    }

    var p = passtCache[seed]
      ? Promise.resolve(passtCache[seed])
      : prendi('complementary').then(function (a) {
          if (a.length >= 2) return a;
          return prendi('related').then(function (b) { return a.concat(b); });
        });

    p.then(function (lista) {
      passtCache[seed] = lista;
      var visti = {}; var scelti = [];
      lista.forEach(function (pr) {
        if (scelti.length >= 2 || visti[pr.id]) return;
        visti[pr.id] = 1;
        if (!pr.available) return;
        if (esclusi.indexOf(String(pr.id)) !== -1) return;
        if (!pr.variants || pr.variants.length !== 1 || !pr.variants[0].available) return;
        scelti.push(pr);
      });
      if (scelti.length === 0) return;
      var list = box.querySelector('[data-sp-passt-list]'); if (!list) return;
      list.textContent = '';
      scelti.forEach(function (pr) {
        var card = document.createElement('div'); card.className = 'sp-passt__card';
        var img = document.createElement('img'); img.className = 'sp-passt__img'; img.alt = ''; img.loading = 'lazy'; img.width = 48; img.height = 48;
        var src = pr.featured_image || '';
        if (src) img.src = src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=120';
        var info = document.createElement('div'); info.className = 'sp-passt__info';
        var a = document.createElement('a'); a.className = 'sp-passt__title'; a.href = root + 'products/' + pr.handle; a.textContent = pr.title;
        var price = document.createElement('span'); price.className = 'sp-passt__price'; price.textContent = soldi(pr.price);
        info.appendChild(a); info.appendChild(price);
        var btn = document.createElement('button'); btn.type = 'button'; btn.className = 'sp-passt__add';
        btn.textContent = '+';
        var txt = document.createElement('span'); txt.className = 'sp-passt__add-txt'; txt.textContent = ' ' + etichetta;
        btn.appendChild(txt);
        btn.setAttribute('aria-label', etichetta + ' – ' + pr.title);
        btn.setAttribute('data-sp-passt-add', pr.variants[0].id);
        card.appendChild(img); card.appendChild(info); card.appendChild(btn);
        list.appendChild(card);
      });
      box.hidden = false;
      misura('sp_cart_passt_shown', { n: scelti.length });
    });
  }

  document.addEventListener('cart-drawer:refreshed', function () { if (cassettoAperto()) riempiPasst(); potaExpress(); });

  /* ── 5) UN solo tasto di pagamento rapido (2026-09-11, capo) ──────────────
     Shopify disegna nel cassetto tutti i portafogli attivi (Apple Pay, PayPal,
     Google Pay) e non permette di sceglierne uno. Qui teniamo quello del
     dispositivo: Apple Pay > Google Pay > PayPal. Lo stile va in linea con
     !important perché Shopify scrive lui il display in linea. I tasti arrivano
     in tempi diversi (skeleton → tasto vero), quindi si riguarda a ogni mutazione. */
  var PREF = ['shopify-apple-pay-button', 'shopify-google-pay-button', 'shopify-paypal-button'];
  function potaExpress() {
    Array.prototype.forEach.call(document.querySelectorAll('.sp-cart-express'), potaExpressBox);
  }
  function potaExpressBox(box) {
    if (!box) return;
    var host = box.querySelector('shopify-accelerated-checkout-cart') || box;
    var figli = Array.prototype.slice.call(host.children);
    var tasti = figli.filter(function (k) { return /^shopify-/i.test(k.tagName); });
    if (tasti.length === 0) return;               /* Shopify non ha ancora disegnato i tasti veri */
    var keep = null;
    for (var i = 0; i < PREF.length && !keep; i++) {
      for (var j = 0; j < tasti.length; j++) { if (tasti[j].tagName.toLowerCase() === PREF[i]) { keep = tasti[j]; break; } }
    }
    keep = keep || tasti[0];
    /* tutto il resto (altri tasti E segnaposto di caricamento) sparisce */
    figli.forEach(function (t) {
      if (t === keep) { t.style.setProperty('display', 'block', 'important'); t.style.setProperty('margin', '0', 'important'); }
      else t.style.setProperty('display', 'none', 'important');
    });
    host.style.setProperty('padding', '0', 'important');
    host.style.setProperty('margin', '0', 'important');
    host.style.setProperty('min-height', '0', 'important');
    /* Shopify riserva comunque il posto del primo tasto (44px + 8px): il tasto tenuto resta
       52px più in basso in un contenitore alto 96. Misurato l'11/09. Rimedio indipendente dai
       suoi CSS: il contenitore è alto quanto il tasto e il resto sale di quel tanto. */
    setTimeout(function () {
      var hb = host.getBoundingClientRect(), kb = keep.getBoundingClientRect();
      var off = Math.round(kb.top - hb.top), kh = Math.round(kb.height);
      if (kh < 20) return;                         /* non ancora disegnato: si riprova alla prossima mutazione */
      box.style.setProperty('height', kh + 'px', 'important');
      box.style.setProperty('overflow', 'hidden', 'important');
      host.style.setProperty('transform', off > 0 ? 'translateY(-' + off + 'px)' : 'none', 'important');
    }, 0);
  }
  window.addEventListener('resize', function () { potaExpress(); });
  (function () {
    var d = cassetto();
    if (d) new MutationObserver(function () { potaExpress(); }).observe(d, { childList: true, subtree: true });
    /* pagina /cart: il blocco dei tasti esiste già, Shopify ci disegna dentro più tardi */
    Array.prototype.forEach.call(document.querySelectorAll('.sp-cart-express'), function (b) {
      if (!b.closest('cart-drawer')) new MutationObserver(function () { potaExpress(); }).observe(b, { childList: true, subtree: true });
    });
    potaExpress();
    riempiPasst();
  })();

  document.addEventListener('click', function (e) {
    var b = e.target && e.target.closest && e.target.closest('[data-sp-passt-add]');
    if (!b) return;
    e.preventDefault();
    if (b.disabled) return;
    b.disabled = true;
    var id = b.getAttribute('data-sp-passt-add');
    fetch(root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }] })
    }).then(function (r) {
      if (!r.ok) return r.json().then(function (j) { throw new Error((j && (j.description || j.message)) || ''); });
      misura('sp_cart_passt_add');
      seq++;                                   /* invalida letture in volo */
      /* nella pagina /cart la lista la disegna il server: si ricarica; nel cassetto basta il refresh */
      if (!b.closest('cart-drawer')) { window.location.reload(); return; }
      document.dispatchEvent(new CustomEvent('cart:refresh'));
    }).catch(function (err) {
      b.disabled = false;
      toast((err && err.message) || genericoText);
    });
  });
})();
